import { useCallback, useEffect, useRef, useState } from 'react';
import { isSplitDefined } from '@/api/getSplitDetails';
import { VITE_WEBHOOK_BASE_URL } from '@/constants/constants';
import { useCheckoutContext } from '@/context/CheckoutContext/CheckoutContext';
import { useCreateSplit } from '@/hooks/useCreateSplit';
import type { useSplitState } from '@/hooks/useSplitState';
import type { Bill as BillDetailsType } from '@/interfaces/Bill';
import { sendBusinessEvent } from '@/screens/Checkout/monitoring';
import { calculateFinalTipAmount } from '@/utils/tipCalculations';
import type { useOutletKitchen } from '../LinkToMenu/useOutletKitchen';
import {
  buildIntentLineItems,
  explodeBillToSplitItems,
  reconcileSelectionWithLocks,
} from '../SplitByItemsMode/splitByItems.util';

interface ItemsSplitPaymentParams {
  bill?: BillDetailsType;
  currency: ReturnType<typeof useOutletKitchen>['currency'];
  splitDetails: ReturnType<typeof useSplitState>['splitDetails'];
  isItemsSplitActive: boolean;
  isSplitExist: boolean;
  isSplitLoading: boolean;
}

/**
 * The split-by-items side of a payment (issue #15, ADR 0003): registering the
 * item universe, the pre-payment step, and the intent lineItems.
 *
 * Apple Pay note: Safari requires `new ApplePaySession()` to run synchronously
 * inside the user-gesture handler, and the SDK awaits `onBeforePayment` before
 * creating the session — so the pre-payment path must not await a network call.
 * Hence the first payer registers the universe when the selection is confirmed
 * (not on the pay tap), the pay button stays hidden until the split exists (see
 * CheckoutController), and onBeforeItemsPayment is a synchronous lock re-check.
 */
export const useItemsSplitPayment = ({
  bill,
  currency,
  splitDetails,
  isItemsSplitActive,
  isSplitExist,
  isSplitLoading,
}: ItemsSplitPaymentParams) => {
  const { mutate: createSplit } = useCreateSplit();
  const {
    state: { selectedLineItemIds, customTipAmount, selectedTipPercentage },
    actions: { confirmItemSplit, toggleSplitBill },
  } = useCheckoutContext();
  const [showItemsConflictAlert, setShowItemsConflictAlert] = useState(false);
  const [showItemsSplitCreationFailedAlert, setShowItemsSplitCreationFailedAlert] = useState(false);
  const registrationRequestedRef = useRef(false);

  // First payer registers the claimable universe (every unit's lineItemId, ids
  // repeating per quantity) the moment a selection is confirmed — off the Apple
  // Pay gesture path. createSplit's onSuccess refreshes the split state, flipping
  // isSplitExist, which reveals the pay button. Items lock at intent creation,
  // not here — createSplit carries no claims.
  useEffect(() => {
    if (!isItemsSplitActive) {
      registrationRequestedRef.current = false;
      return;
    }
    if (isSplitLoading || isSplitExist || selectedLineItemIds.length === 0) {
      return;
    }
    if (registrationRequestedRef.current || !bill) {
      return;
    }

    registrationRequestedRef.current = true;
    const items = explodeBillToSplitItems(bill.positions);
    createSplit(
      {
        totalAmount: bill.baseTotal,
        currency,
        reference: bill.orderNumber,
        type: 'SPLIT_BY_ITEMS',
        lineItemIds: items.map(item => item.lineItemId),
        webhookUrl: `${VITE_WEBHOOK_BASE_URL}/api/dine-in-public/payments-api/bills/${bill.billId}/payments`,
      },
      // A failed attempt clears the guard so the next render can retry, rather
      // than wedging the pay button hidden forever
      {
        onError: () => {
          registrationRequestedRef.current = false;
          sendBusinessEvent('kitopay.split_bill.creation_failed', { splitType: 'SPLIT_BY_ITEMS' });
          setShowItemsSplitCreationFailedAlert(true);
          setTimeout(() => setShowItemsSplitCreationFailedAlert(false), 6000);
        },
      }
    );
  }, [
    isItemsSplitActive,
    isSplitLoading,
    isSplitExist,
    selectedLineItemIds,
    bill,
    currency,
    createSplit,
  ]);

  // Pre-payment lock re-check: drop any units another payer locked since Confirm.
  // If that empties part of the selection, abort the intent and reopen the picker.
  // Reads the polled split state (no fetch) so it stays synchronous and never
  // breaks the Apple Pay gesture. The backend rejects over-claims atomically
  // (BE-3) anyway — this is the friendlier toast-and-repick path.
  const onBeforeItemsPayment = useCallback((): { orderHasBeenChanged: boolean } => {
    if (!bill) {
      return { orderHasBeenChanged: false };
    }

    const items = explodeBillToSplitItems(bill.positions, bill.grossDiscount || 0);
    const lockedLineItemIds =
      (isSplitDefined(splitDetails) && splitDetails.lockedLineItemIds) || [];

    const reconciled = reconcileSelectionWithLocks(items, selectedLineItemIds, lockedLineItemIds);
    if (reconciled.hasConflict) {
      confirmItemSplit(
        reconciled.selectedLineItemIds,
        reconciled.selectedAmount,
        calculateFinalTipAmount(customTipAmount, reconciled.selectedAmount, selectedTipPercentage)
      );
      toggleSplitBill();
      setShowItemsConflictAlert(true);
      setTimeout(() => {
        setShowItemsConflictAlert(false);
      }, 6000);
      return { orderHasBeenChanged: true };
    }

    return { orderHasBeenChanged: false };
  }, [
    bill,
    splitDetails,
    selectedLineItemIds,
    customTipAmount,
    selectedTipPercentage,
    confirmItemSplit,
    toggleSplitBill,
  ]);

  // The confirmed selection in intent wire shape, priced after the locks the
  // polled split state knows about
  const buildItemsLineItems = useCallback(() => {
    if (!bill) {
      return [];
    }

    const lockedLineItemIds =
      (isSplitDefined(splitDetails) && splitDetails.lockedLineItemIds) || [];

    return buildIntentLineItems(
      explodeBillToSplitItems(bill.positions, bill.grossDiscount || 0),
      selectedLineItemIds,
      lockedLineItemIds
    );
  }, [bill, splitDetails, selectedLineItemIds]);

  return {
    onBeforeItemsPayment,
    buildItemsLineItems,
    showItemsConflictAlert,
    showItemsSplitCreationFailedAlert,
  };
};
